import { logger } from "@octopusdeploy/logging";
import type { EventResource, EventReference } from "@octopusdeploy/octopus-server-client";
import { AccountType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Url } from "@octopusdeploy/portal-routes";
import { memoize, forIn, omit } from "lodash";
import * as React from "react";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
interface EventFormatterProps {
    event: EventResource;
}
interface ResourceLinkItem {
    prefix: string;
    redirect(id: string): string;
}
interface Index {
    reference?: EventReference | null;
    index: number;
}
interface Part {
    reference?: EventReference | null;
    message: string;
}
export type NonLinkableDocumentType = {
    type: "non-linkable";
};
export function isNonLinkableDocumentType(link: string | Url | NonLinkableDocumentType): link is NonLinkableDocumentType {
    return typeof link !== "string" && "type" in link && link.type === "non-linkable";
}
const documentTypeToLinkLookups = memoize((spaceId?: string) => linksFor(spaceId));
function isNonLinkable(documentId: string | null, documentType: string | null): boolean {
    // We don't have a page to show these types for a user, so please don't give them a link.
    switch (documentType) {
        case "artifacts":
        case "apikeys":
        case "workertaskleases":
        case "gitentitystashs":
        case "deploymentfreezeoverrides":
        case "features": // Refers to a project's feature toggles. We lack a redirect here.
        case "invitations":
        case "projectintentss":
        case "retention":
        case "sampleprojects":
        case "serviceaccountoidcidentitys":
        case "variableset": // Refers to a project's variableset. We lack a redirect here.
        case "kubernetesexecutionsensitivevalues":
        case "kubernetesresourcemanifests":
        case "kubernetesmonitors":
        case "activecompliancepolicyversions":
        case "compliancepolicyversions":
        case "processtemplateshares":
        case "processtemplateversions":
            return true;
        default:
            return false;
    }
}
export function linkTo(documentId: string | null, documentType: string | null, spaceId?: string): string | Url | NonLinkableDocumentType {
    if (!documentId || !documentType || isNonLinkable(documentId, documentType)) {
        return { type: "non-linkable" };
    }
    const documentTypeSpecificLink = documentTypeToLinkLookups(spaceId)[documentType];
    if (!documentTypeSpecificLink) {
        logger.error("Unable to find document type to link mapping for '{documentType}' in event formatter", {
            documentId,
            documentType,
            spaceId,
        });
        return "/not-found";
    }
    return documentTypeSpecificLink(documentId);
}
export const EventFormatter: React.FC<EventFormatterProps> = (props) => {
    const message = props.event.Message;
    const references = props.event.MessageReferences;
    const indexes: Index[] = !references || references.length === 0
        ? [
            { reference: null, index: 0 },
            { reference: null, index: message.length },
        ]
        : toIndexes(references, message);
    let last: Index | null = null;
    const parts: Part[] = [];
    indexes.map((index) => {
        if (last) {
            parts.push({ reference: last.reference, message: message.substring(last.index, index.index) });
        }
        last = index;
    });
    const encodedParts = parts.map((part) => {
        const encodedMessage = <span>{part.message}</span>;
        if (part.reference) {
            const documentId = part.reference.ReferencedDocumentId;
            const documentType = retrieveDocumentTypeFromId(documentId);
            const link = linkTo(documentId, documentType, props.event.SpaceId);
            if (!isNonLinkableDocumentType(link)) {
                return <InternalLink to={link}>{encodedMessage}</InternalLink>;
            }
        }
        return encodedMessage;
    });
    return <span>{React.Children.toArray(encodedParts)}</span>;
};
EventFormatter.displayName = "EventFormatter"
export function retrieveDocumentTypeFromId(documentId: string): string | null {
    const index = documentId.indexOf("-");
    return index === -1 ? null : documentId.substr(0, index).toLowerCase();
}
function toIndexes(references: EventReference[], message: string) {
    const indexes: Index[] = [];
    const refStart = references[0];
    if (refStart.StartIndex !== 0) {
        indexes.push({ reference: null, index: 0 });
    }
    let lastRef: EventReference | null = null;
    references.forEach((reference) => {
        if (!lastRef || lastRef.StartIndex !== reference.StartIndex) {
            indexes.push({ reference, index: reference.StartIndex });
        }
        indexes.push({ reference: null, index: reference.StartIndex + reference.Length });
        lastRef = reference;
    });
    const refEnd = references[references.length - 1];
    const endIndex = refEnd.StartIndex + refEnd.Length;
    if (endIndex !== message.length) {
        indexes.push({ reference: null, index: message.length });
    }
    return indexes;
}
type DocumentTypeToLinkLookup = {
    [prefix: string]: (id: string) => string | Url;
};
function linksFor(spaceId?: string): DocumentTypeToLinkLookup {
    const lookup: DocumentTypeToLinkLookup = {};
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["accounts"] = (id) => links.accountPage.generateUrl({ spaceId: spaceId!, accountId: id }); // Accounts are coming through with Accounts-1, Accounts-21 IDs now (apparently).
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["actiontemplates"] = (id) => links.stepTemplateDetailsPage.generateUrl({ spaceId: spaceId!, templateId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["deployments"] = (id) => links.deploymentToProjectTaskRedirect.generateUrl({ spaceId: spaceId!, deploymentId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["environments"] = (id) => links.environmentPage.generateUrl({ spaceId: spaceId!, environmentId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["feeds"] = (id) => links.redirectFeedBasedOnType.generateUrl({ spaceId: spaceId!, feedId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["libraryvariablesets"] = (id) => links.variableSetPage.generateUrl({ spaceId: spaceId!, variableSetId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["lifecycles"] = (id) => links.lifecyclePage.generateUrl({ spaceId: spaceId!, lifecycleId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["machinepolicies"] = (id) => links.machinePolicyPage.generateUrl({ spaceId: spaceId!, machinePolicyId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["machines"] = (id) => links.deploymentTargetSettingsPage.generateUrl({ spaceId: spaceId!, machineId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["workers"] = (id) => links.workerSettingsPage.generateUrl({ spaceId: spaceId!, machineId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["workerpools"] = (id) => links.workerPoolPage.generateUrl({ spaceId: spaceId!, workerPoolId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["projects"] = (id) => links.projectRootRedirect.generateUrl({ spaceId: spaceId!, projectSlug: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["projectgroups"] = (id) => links.projectGroupPage.generateUrl({ spaceId: spaceId!, projectGroupId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["proxys"] = (id) => links.machineProxyPage.generateUrl({ spaceId: spaceId!, proxyId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["proxies"] = (id) => links.machineProxyPage.generateUrl({ spaceId: spaceId!, proxyId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["releases"] = (id) => links.releaseRedirect.generateUrl({ spaceId: spaceId!, releaseId: id });
    lookup["servertasks"] = (id) => links.taskPage.generateUrl({ taskId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["tagsets"] = (id) => links.tagSetPage.generateUrl({ spaceId: spaceId!, tagSetId: id });
    lookup["teams"] = (id) => links.teamPage.generateUrl({ teamId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["tenants"] = (id) => links.tenantOverviewPage.generateUrl({ spaceId: spaceId!, tenantId: id });
    lookup["users"] = (id) => links.userPage.generateUrl({ userId: id });
    lookup["userroles"] = (id) => links.userRolePage.generateUrl({ roleId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["accounts"] = (id) => links.accountPage.generateUrl({ spaceId: spaceId!, accountId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    forIn(omit(AccountType, AccountType.None), (value, key) => (lookup[value.toLowerCase()] = (id) => links.accountPage.generateUrl({ spaceId: spaceId!, accountId: id })));
    lookup["subscriptions"] = (id) => links.subscriptionPage.generateUrl({ subscriptionId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["certificates"] = (id) => links.certificatePage.generateUrl({ spaceId: spaceId!, certificateId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["projecttriggers"] = (id) => links.triggerRedirect.generateUrl({ spaceId: spaceId!, triggerId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["channels"] = (id) => links.channelRedirect.generateUrl({ spaceId: spaceId!, channelId: id });
    lookup["spaces"] = (id) => links.spacePage.generateUrl({ currentSpaceId: id });
    lookup["scopeduserroles"] = (id) => links.scopedUserRoleToTeamRedirect.generateUrl({ scopedRoleId: id });
    lookup["octopusservernodes"] = (id) => links.nodesPage.generateUrl();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["runbookruns"] = (id) => links.runbookRunRedirect.generateUrl({ spaceId: spaceId!, runbookRunId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["runbooksnapshots"] = (id) => links.runbookSnapshotRedirect.generateUrl({ spaceId: spaceId!, runbookSnapshotId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["runbooks"] = (id) => links.runbookRedirect.generateUrl({ spaceId: spaceId!, runbookId: id });
    lookup["interruptions"] = (id) => links.interruptionToTaskRedirect.generateUrl({ interruptionId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["deploymentsettings"] = (id) => links.deploymentSettingsPage.generateUrl({ spaceId: spaceId!, projectSlug: id.replace(/deploymentsettings-/i, "") });
    lookup["octopusservernodestatuss"] = (id) => links.nodesPage.generateUrl();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["buildinformation"] = (id) => links.buildInformationDetailsPage.generateUrl({ spaceId: spaceId!, id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["githubappconnections"] = (id) => links.gitHubConnectionPage.generateUrl({ spaceId: spaceId!, connectionId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["deploymentprocess"] = (id) => links.deploymentProcessPage.generateUrl({ spaceId: spaceId!, projectSlug: id.replace(/deploymentprocess-/i, "") });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["dashboardconfigurations"] = (id) => links.projectsPage.generateUrl({ spaceId: spaceId! });
    lookup["deploymentfreezes"] = (id) => links.editDeploymentFreezePage.generateUrl({ deploymentFreezeId: id });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    lookup["gitcredentials"] = (id) => links.gitCredentialPage.generateUrl({ spaceId: spaceId!, gitCredentialId: id });
    return lookup;
}
export default EventFormatter;
